import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from '../Header'; // Main site header
import AdminSidebar from './AdminSidebar';
import './AdminLayout.css';

const AdminLayout = () => {
  // State to manage sidebar collapse, controlled by user toggle
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(132); // Default height
  const headerRef = useRef(null); // Reference to the header element
  const location = useLocation(); // Get current location for route change detection

  // Calculate header height on mount and window resize
  useEffect(() => {
    const calculateHeaderHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        
        // Update CSS variable for use in styles
        document.documentElement.style.setProperty('--header-height', `${height}px`);
        
        // Force layout recalculation
        document.body.style.setProperty('--force-repaint', 'true');
        setTimeout(() => {
          document.body.style.removeProperty('--force-repaint');
        }, 10);
      }
    };

    // Initial calculation
    calculateHeaderHeight();
    
    // Recalculate on window resize
    window.addEventListener('resize', calculateHeaderHeight);
    
    // Set multiple timeouts to ensure header height is calculated correctly
    // after all content and styles are fully loaded
    const timeoutIds = [
      setTimeout(calculateHeaderHeight, 100),
      setTimeout(calculateHeaderHeight, 300),
      setTimeout(calculateHeaderHeight, 500),
      setTimeout(calculateHeaderHeight, 1000)
    ];
    
    // Create a ResizeObserver to detect changes in the header's size
    const resizeObserver = new ResizeObserver(() => {
      calculateHeaderHeight();
    });
    
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', calculateHeaderHeight);
      timeoutIds.forEach(id => clearTimeout(id));
      resizeObserver.disconnect();
    };
  }, []);

  // Force recalculation when route changes
  useEffect(() => {
    // This will ensure the header height is recalculated when navigating between admin pages
    const recalculateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };
    
    // Use requestAnimationFrame to ensure the DOM has been painted
    requestAnimationFrame(() => {
      recalculateHeight();
      // Add multiple delays to ensure all styles are applied
      const timeoutIds = [
        setTimeout(recalculateHeight, 100),
        setTimeout(recalculateHeight, 300),
        setTimeout(recalculateHeight, 500)
      ];
      
      return () => {
        timeoutIds.forEach(id => clearTimeout(id));
      };
    });
  }, [location.pathname]);

  // Function to toggle the sidebar state
  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  return (
    <div className={`admin-layout ${isSidebarCollapsed ? 'sidebar-collapsed-layout' : ''}`}>
      {/* Pass ref to Header to measure its height */}
      <div ref={headerRef} className="admin-header-container">
        <Header />
      </div>
      
      <div className="admin-layout-body">
        {/* Sidebar positioned relative to the layout body */}
        <AdminSidebar 
          collapsed={isSidebarCollapsed} 
          toggleSidebar={toggleSidebar} 
        />
        
        <main className={`admin-main-content ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          <Outlet /> {/* Renders the specific admin page component */}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
